/**
 * Testi italiani del guscio: marchio, navigazione, intestazione, indirizzo
 * sconosciuto.
 *
 * Il nome del prodotto sta qui e non come stringa scritta nei componenti
 * perché compare in cinque punti (barra laterale, cassetto, intestazione
 * stretta, pagina di accesso, titolo del documento) e deve essere uno solo.
 * Non si traduce: è un nome proprio.
 */

/** Testi del guscio dell'applicazione. */
export const app = {
  /** Nome del prodotto. Non si traduce. */
  brand: 'institor',

  /** Collegamento che salta il menu e porta al contenuto. */
  skipLink: 'Vai al contenuto',

  /** Navigazione principale, a gruppi (ADR 0030 §2.1). */
  nav: {
    /** Nome accessibile della barra laterale. */
    label: 'Aree della dashboard',
    /** Pulsante che apre il menu sugli schermi stretti. */
    open: 'Apri il menu',
    /** Pagina iniziale con ciò che chiede un intervento. */
    overview: 'Panoramica',
    /** Gruppo del set corrente. */
    currentSet: 'Set corrente',
    /** Segnaposto del selettore quando nessun set è scelto. */
    pickSet: 'Scegli un set',
    /** Selettore senza risultati. */
    noSetFound: 'Nessun set trovato',
    /** Sezione del relay. */
    relay: 'Institore',
    /** Sezione dei task. */
    tasks: 'Task',
    /** Testo per gli assistivi accanto al contatore delle proposte. */
    proposals_one: '{{count}} proposta da approvare',
    /** Testo per gli assistivi accanto al contatore delle proposte. */
    proposals_other: '{{count}} proposte da approvare',
    /** Sezione dei siti dei progetti. */
    artifacts: 'Artefatti',
    /** Sezione dei progetti del set. */
    setProjects: 'Progetti',
    /** Sezione degli agenti del set. */
    agents: 'Agenti',
    /** Sezione delle impostazioni del set. */
    setSettings: 'Impostazioni del set',
    /** Gruppo del lavoro su tutti i set. */
    work: 'Lavoro',
    /** Elenco di tutti i set. */
    sets: 'Tutti i set',
    /** Cronologia dei messaggi. */
    history: 'Cronologia',
    /** Gruppo delle risorse. */
    resources: 'Risorse',
    /** Strumenti collegati dall'utente. */
    tools: 'Strumenti',
    /** Template di sito in anteprima. */
    templates: 'Template',
    /** Catalogo dei ruoli degli agenti. */
    roles: 'Ruoli',
    /** Gruppo dell'account. */
    account: 'Account',
    /** Abbonamento, consumo e pagamenti (D5: una voce sola). */
    billing: 'Abbonamento',
    /** Gruppo dell'amministrazione, solo per gli amministratori. */
    admin: 'Amministrazione',
    /** Utenti della piattaforma. */
    users: 'Utenti',
    /** Tutti i progetti, nelle briciole di pane delle pagine di un progetto. */
    projects: 'Progetti',
    /** Account Google e GitHub di institor. */
    platform: 'Piattaforma',
    /** Grafana e allarmi. */
    monitoring: 'Monitoraggio',
    /** Riquadro «Nuovo set» in fondo alla barra laterale (ADR 0021): frase sotto il titolo. */
    newSetHint: 'Crea un gruppo di agenti per un nuovo lavoro.',
    /** Pulsante che riduce la barra laterale alle sole icone. */
    collapse: 'Riduci il menu',
    /** Pulsante che riapre la barra laterale ridotta. */
    expand: 'Espandi il menu',
    /** Barra ridotta: pulsante che la riapre per cambiare set. */
    changeSet: 'Cambia set ({{name}})',
  },

  /** Pagina riservata agli amministratori, aperta da chi non lo è. */
  adminOnly: {
    /** Titolo. */
    title: 'Pagina riservata',
    /** Spiegazione. */
    subtitle: 'Questa pagina è per gli amministratori della piattaforma.',
  },

  /**
   * Banner sempre visibile mentre si usa l'account di un altro utente
   * (ADR 0009). Dice di chi è l'account, chi c'è dietro e quando finisce.
   */
  impersonation: {
    /** Nome accessibile della fascia. */
    label: 'Accesso come un altro utente',
    /** Frase principale. */
    notice: "Stai usando l'account di {{user}} come {{admin}}.",
    /** Scadenza automatica. */
    expires: 'Torni al tuo account alle {{time}}.',
    /** Pulsante di ritorno: testo chiesto dall'utente (quattro parole, deroga dichiarata). */
    stop: 'Torna al tuo account',
    /** Ritorno non riuscito. */
    stopFailed: 'Ritorno non riuscito. Riprova.',
  },

  /** Comandi dell'intestazione. */
  header: {
    /** Badge del piano dell'utente (ADR 0023), con il nome del piano. */
    plan: 'Piano {{plan}}',
    /** Badge dell'amministratore della piattaforma, che non ha limiti di piano. */
    unrestricted: 'Senza limiti',
  },

  /** Selettore della lingua. */
  language: {
    /** Nome accessibile del selettore. */
    label: 'Lingua',
    /** Italiano, scritto in italiano anche nell'interfaccia inglese. */
    it: 'Italiano',
    /** Inglese, scritto in inglese anche nell'interfaccia italiana. */
    en: 'English',
    /** Tedesco, scritto in tedesco. */
    de: 'Deutsch',
    /** Spagnolo, scritto in spagnolo. */
    es: 'Español',
    /** Russo, scritto in russo. */
    ru: 'Русский',
    /** Coreano, scritto in coreano. */
    ko: '한국어',
    /** Tailandese, scritto in tailandese. */
    th: 'ไทย',
  },

  /** Selettore del tema chiaro o scuro. */
  theme: {
    /** Nome accessibile del selettore. */
    label: 'Tema',
    /** Stato che segue la preferenza del sistema operativo. */
    system: 'Sistema',
    /** Stato chiaro, scelto esplicitamente. */
    light: 'Chiaro',
    /** Stato scuro, scelto esplicitamente. */
    dark: 'Scuro',
  },

  /** Pagina mostrata per un indirizzo che non esiste. */
  notFound: {
    /** Titolo della pagina. */
    title: 'Pagina non trovata',
    /** Spiegazione sotto il titolo. */
    subtitle: "L'indirizzo non corrisponde a nessuna pagina della dashboard.",
  },
};

/** Forma dei testi del guscio, usata per tipare le altre lingue. */
export type App = typeof app;
